import React from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { Typography, Paper, Box, Stack, Grid } from '@mui/material';
import { FieldArray, Formik, Form } from 'formik';
import { useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import * as yup from 'yup';

import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomButton } from '../../../../components/CustomButton';
import { createCompany } from '../../../../store/company/actions';

export const CreateCompany = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const validationSchema = yup.object({
    name: yup
      .string()
      .min(2, 'Минимум 2 символа')
      .max(50, 'Максимум 50 символов')
      .test('symbols', 'Допустимые символы: 0-9, Ая-Яя, Aa-Zz, ", -, _, @, пробел', val => /^[ а-яА-Яa-zA-Z0-9-_@"]+$/.test(val))
      .required('Обязательное поле'),
    bin: yup
      .string()
      .min(12, 'Недопустимый формат ввода данных')
      .max(12, 'Недопустимый формат ввода данных')
      .required('Обязательное поле'),
    phones: yup.array().of(
      yup.string().test('len', 'Must be exactly 15 characters', val => {
        return val?.length === 15 || val === undefined;
      })
    )
  });

  return (
    <>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        Создать мою компанию
      </Typography>

      <Paper
        sx={{
          padding: '24px'
        }}>
        <Formik
          initialValues={{
            name: null,
            bin: null,
            contact_person: '',
            email: '',
            phones: [''],
            city: ''
          }}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={(values) => {
            const transformData = {
              ...values,
              phones: values.phones.filter((item) => item !== '' && item !== null)
            };

            dispatch(createCompany(transformData));
            history.push(ROUTES.COMPANY);
          }}
          render={({ values, handleChange, setFieldValue, errors, isValid, dirty }) => (
            <Form>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '24px',
                  maxWidth: '500'
                }}>
                <Box sx={{ display: 'flex', marginBottom: '24px' }}>
                  <Stack spacing={2} justifyContent="center">
                    <CustomInput
                      name="name"
                      required
                      value={values.name}
                      placeholder="Наименование *"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      error={errors['name']}
                      helperText={errors['name']}
                    />
                    <CustomInput
                      name="bin"
                      required
                      value={values.bin}
                      placeholder="БИН *"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      type="number"
                      onInput={(e) => {
                        e.target.value = Math.max(0, parseInt(e.target.value))
                          .toString()
                          .slice(0, 12);
                      }}
                      error={errors['bin']}
                      helperText={errors['bin']}
                    />
                  </Stack>
                </Box>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInput
                      name="contact_person"
                      value={values.contact_person}
                      placeholder="Контактное лицо"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 496 }}
                      size="small"
                      helperText="ФИО контактного лица компании"
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInput
                      name="email"
                      value={values.email}
                      placeholder="Email"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 496 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sm={6} mb="24px">
                  <FieldArray
                    name="phones"
                    validateOnChange={false}
                    render={(arrayHelpers) => (
                      <>
                        {values?.phones.map((phone, index) => (
                          <Grid item key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 240 }}>
                              <CustomInputWithMask
                                name={`phones.${index}`}
                                value={phone}
                                placeholder="Номер телефона"
                                onChange={handleChange}
                                onKeyDown={(event) => {if (!phone && event.key === 'Backspace' && values.phones.length !== 1) arrayHelpers.remove(index)}}
                                variant="outlined"
                                sx={{ width: 240 }}
                                size="small"
                              />
                              {values.phones.length === index + 1 && index < 4 && (
                                <AddIcon
                                  sx={{ color: 'primary.main' }}
                                  onClick={() => arrayHelpers.push()}
                                  data-testid="AddPhone"
                                />
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </>
                    )}
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid item>
                    <CustomInput
                      name="city"
                      value={values.city}
                      placeholder="Город"
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      sx={{ width: 496 }}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Box>
                  <CustomButton
                    component={RouterLink}
                    to={ROUTES.PROFILE}
                    color="secondary"
                    variant="contained"
                    sx={{ marginRight: '16px' }}>
                    Отмена
                  </CustomButton>
                </Box>

                <CustomButton disabled={!dirty || !isValid} type="submit" variant="contained">
                  Сохранить
                </CustomButton>
              </Box>
            </Form>
          )}
        />
      </Paper>
    </>
  );
};
